import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { Anomaly } from '@soc/shared';

/**
 * Client-owned alert store, grouped by rule so a rule's Stop can drop its alerts
 * in O(1). The detection engine appends each tick's results here; the feed + map
 * render from a flattened selector (see selectors.ts). Ephemeral — nothing is
 * persisted, so a reload starts empty.
 */
interface AlertsState {
  byRule: Record<string, Anomaly[]>;
}

/** Per-rule cap so one noisy rule can't starve the others. */
const MAX_PER_RULE = 200;

const initialState: AlertsState = { byRule: {} };

const alertsSlice = createSlice({
  name: 'alerts',
  initialState,
  reducers: {
    /**
     * Write a tick's anomalies into a rule's group.
     *
     * Default (live rules): MERGE — de-dupe by id against what's there, newest-first,
     * cap. Successive ticks accumulate so the feed keeps trickling.
     *
     * `replace: true` (single-shot Fetch): REPLACE the rule's group with just this
     * run's results, so a second Fetch shows a fresh snapshot instead of piling a
     * duplicate card on top of the previous one (the id embeds the window, so the
     * merge path would never dedupe across two Fetches). Done in one reducer so the
     * swap is atomic — no empty-then-refill flicker while the request is in flight.
     */
    alertsAppended(
      state,
      action: PayloadAction<{ ruleId: string; anomalies: Anomaly[]; replace?: boolean }>,
    ) {
      const { ruleId, anomalies, replace } = action.payload;
      const existing = replace ? [] : (state.byRule[ruleId] ?? []);
      const seen = new Set(existing.map((a) => a.id));
      const fresh = anomalies.filter((a) => !seen.has(a.id));
      // On replace, always commit (even an empty result clears the old snapshot);
      // on merge, a no-new-alerts tick is a no-op so we don't churn the array.
      if (!replace && fresh.length === 0) return;
      const merged = [...fresh, ...existing].sort((a, b) => b.timestamp.localeCompare(a.timestamp));
      if (merged.length > MAX_PER_RULE) merged.length = MAX_PER_RULE;
      state.byRule[ruleId] = merged;
    },
    /** Drop a rule's alerts entirely — used on Stop. */
    ruleCleared(state, action: PayloadAction<string>) {
      delete state.byRule[action.payload];
    },
    /** Clear every rule's alerts (feed "Clear all"). */
    allCleared(state) {
      state.byRule = {};
    },
    /** Mark one alert acknowledged (dims it in the feed, drops it from map pins). */
    alertAcknowledged(state, action: PayloadAction<string>) {
      for (const list of Object.values(state.byRule)) {
        const hit = list.find((a) => a.id === action.payload);
        if (hit) {
          hit.acknowledged = true;
          break;
        }
      }
    },
    /** Acknowledge every alert across all rules. */
    allAcknowledged(state) {
      for (const list of Object.values(state.byRule)) {
        for (const a of list) a.acknowledged = true;
      }
    },
  },
});

export const { alertsAppended, ruleCleared, allCleared, alertAcknowledged, allAcknowledged } =
  alertsSlice.actions;
export default alertsSlice.reducer;
